import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useLocalStorage } from "@uidotdev/usehooks";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatDateAndTime } from "../utils/dateUtils";
import { isAdminUser } from "../utils/access";
import isDesktop from "../isDesktop";
import Sidebar from "../sidenav/Sidenav";
import { ReactComponent as UserIcon } from "../assets/icons/UserIcon.svg";
import { ReactComponent as Hemberger } from "../assets/icons/Hemberger.svg";
import loginAnalyticsService from "./loginAnalyticsService";
import "./LoginAnalytics.css";

const TABLE_LIMIT = 25;

const toDateInputValue = (date) => {
  const value = new Date(date);
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 6);
  return {
    from: toDateInputValue(from),
    to: toDateInputValue(to),
  };
};

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

const formatPercent = (value) => {
  const number = Number(value || 0);
  return `${number.toFixed(1)}%`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const formatted = formatDateAndTime(value);
  return `${formatted.date} ${formatted.time}`;
};

const displayFailureReason = (value) => value || "-";

const prettifyFailureReason = (value) => {
  if (!value) return "-";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const errorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const StatusChip = ({ status }) => {
  const normalized = String(status || "").toLowerCase();
  const failed = normalized === "failed";
  const label = normalized === "success" ? "Success" : failed ? "Failed" : status || "-";

  return (
    <Chip
      size="small"
      label={label}
      color={failed ? "warning" : normalized === "success" ? "success" : "default"}
      variant={failed ? "filled" : "outlined"}
    />
  );
};

const DataState = ({ loading, error, empty, emptyTitle, emptyText, retry, children }) => {
  if (loading) {
    return (
      <div className="login-analytics-loading">
        <CircularProgress aria-label="Loading login analytics" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          retry ? (
            <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={retry}>
              Retry
            </Button>
          ) : null
        }
      >
        {error}
      </Alert>
    );
  }

  if (empty) {
    return (
      <div className="login-analytics-empty">
        <strong>{emptyTitle}</strong>
        <span>{emptyText}</span>
      </div>
    );
  }

  return children;
};

const LoginAnalytics = () => {
  const [token] = useLocalStorage("token", null);
  const defaultRange = useMemo(getDefaultRange, []);
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);
  const [groupBy, setGroupBy] = useState("day");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [failureReason, setFailureReason] = useState("");
  const [usersPage, setUsersPage] = useState(0);
  const [recentPage, setRecentPage] = useState(0);

  const [summary, setSummary] = useState(null);
  const [failureReasons, setFailureReasons] = useState([]);
  const [trends, setTrends] = useState([]);
  const [users, setUsers] = useState([]);
  const [recentAttempts, setRecentAttempts] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [recentTotal, setRecentTotal] = useState(0);
  const [mainLoading, setMainLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [recentLoading, setRecentLoading] = useState(true);
  const [mainError, setMainError] = useState("");
  const [usersError, setUsersError] = useState("");
  const [recentError, setRecentError] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isDesktopDevice = isDesktop();
  const canViewAnalytics = isAdminUser();

  const rangeParams = useMemo(() => ({ from, to }), [from, to]);

  const failureReasonOptions = useMemo(() => {
    const values = failureReasons.map((item) => item.reason).filter(Boolean);
    if (failureReason && !values.includes(failureReason)) values.unshift(failureReason);
    return values;
  }, [failureReason, failureReasons]);

  const loadMainAnalytics = useCallback(async () => {
    if (!token || !canViewAnalytics) return;
    setMainLoading(true);
    setMainError("");
    try {
      const [summaryResult, failureResult, trendResult] = await Promise.all([
        loginAnalyticsService.getSummary(token, rangeParams),
        loginAnalyticsService.getFailureReasons(token, rangeParams),
        loginAnalyticsService.getTrends(token, { ...rangeParams, groupBy }),
      ]);
      setSummary(summaryResult?.data || {});
      setFailureReasons(Array.isArray(failureResult?.data) ? failureResult.data : []);
      setTrends(Array.isArray(trendResult?.data) ? trendResult.data : []);
    } catch (error) {
      setSummary(null);
      setFailureReasons([]);
      setTrends([]);
      setMainError(errorMessage(error, "Unable to load login analytics."));
    } finally {
      setMainLoading(false);
    }
  }, [canViewAnalytics, groupBy, rangeParams, token]);

  const loadUsers = useCallback(async () => {
    if (!token || !canViewAnalytics) return;
    setUsersLoading(true);
    setUsersError("");
    try {
      const result = await loginAnalyticsService.getUsers(token, {
        ...rangeParams,
        page: usersPage + 1,
        limit: TABLE_LIMIT,
        search: search.trim(),
      });
      setUsers(Array.isArray(result?.data) ? result.data : []);
      setUsersTotal(Number(result?.pagination?.total || 0));
    } catch (error) {
      setUsers([]);
      setUsersTotal(0);
      setUsersError(errorMessage(error, "Unable to load problem users."));
    } finally {
      setUsersLoading(false);
    }
  }, [canViewAnalytics, rangeParams, search, token, usersPage]);

  const loadRecentAttempts = useCallback(async () => {
    if (!token || !canViewAnalytics) return;
    setRecentLoading(true);
    setRecentError("");
    try {
      const params = {
        ...rangeParams,
        page: recentPage + 1,
        limit: TABLE_LIMIT,
      };
      if (status !== "all") params.status = status;
      if (failureReason) params.failureReason = failureReason;

      const result = await loginAnalyticsService.getRecentAttempts(token, params);
      setRecentAttempts(Array.isArray(result?.data) ? result.data : []);
      setRecentTotal(Number(result?.pagination?.total || 0));
    } catch (error) {
      setRecentAttempts([]);
      setRecentTotal(0);
      setRecentError(errorMessage(error, "Unable to load recent login attempts."));
    } finally {
      setRecentLoading(false);
    }
  }, [canViewAnalytics, failureReason, rangeParams, recentPage, status, token]);

  useEffect(() => {
    loadMainAnalytics();
  }, [loadMainAnalytics]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadRecentAttempts();
  }, [loadRecentAttempts]);

  useEffect(() => {
    setUsersPage(0);
  }, [from, search, to]);

  useEffect(() => {
    setRecentPage(0);
  }, [failureReason, from, status, to]);

  const summaryCards = [
    ["Total Attempts", formatNumber(summary?.totalAttempts)],
    ["Successful Logins", formatNumber(summary?.successfulLogins)],
    ["Failed Logins", formatNumber(summary?.failedLogins)],
    ["Failure Rate", formatPercent(summary?.failureRate)],
    ["Unique Usernames", formatNumber(summary?.uniqueUsernames)],
    ["Unknown Usernames", formatNumber(summary?.unknownUsernames)],
  ];

  const hasAnyData =
    Number(summary?.totalAttempts || 0) > 0 ||
    trends.length > 0 ||
    failureReasons.length > 0 ||
    users.length > 0 ||
    recentAttempts.length > 0;

  if (!canViewAnalytics) {
    return (
      <div className="login-analytics-page">
        <Alert severity="error">You are not authorized to access Login Analytics.</Alert>
      </div>
    );
  }

  return (
    <div className="login-analytics-page">
      {!isDesktopDevice && (
        <>
          <div className="login-analytics-mobile-nav">
            <button
              type="button"
              className="login-analytics-menu-button"
              aria-label="Open navigation"
              onClick={() => setIsSidebarOpen(true)}
            >
              <Hemberger />
            </button>
            <Link to="/profile" aria-label="View profile">
              <UserIcon />
            </Link>
          </div>
          <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        </>
      )}
      <div className="login-analytics-shell">
        <div className="login-analytics-header">
          <div>
            <Typography variant="h4" component="h1">
              Login Analytics
            </Typography>
            <Typography color="text.secondary">
              Review login attempts, failures, and users with repeated problems.
            </Typography>
          </div>
          <div className="login-analytics-filters" aria-label="Login analytics filters">
            <TextField
              label="From"
              type="date"
              size="small"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </div>
        </div>

        <DataState
          loading={mainLoading}
          error={mainError}
          empty={!hasAnyData}
          emptyTitle="No login attempts found"
          emptyText="Try a different date range to view login analytics."
          retry={loadMainAnalytics}
        >
          <div className="login-analytics-summary-grid">
            {summaryCards.map(([label, value]) => (
              <div className="login-analytics-summary-card" key={label}>
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>

          <div className="login-analytics-chart-grid">
            <section className="login-analytics-panel" aria-labelledby="login-trend-title">
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                <h2 id="login-trend-title">Login Attempts Trend</h2>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel id="group-by-label">Group by</InputLabel>
                  <Select
                    labelId="group-by-label"
                    label="Group by"
                    value={groupBy}
                    onChange={(event) => setGroupBy(event.target.value)}
                  >
                    <MenuItem value="day">Day</MenuItem>
                    <MenuItem value="hour">Hour</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <DataState
                loading={false}
                empty={trends.length === 0}
                emptyTitle="No trend data"
                emptyText="There are no trend points for this range."
              >
                <div className="login-analytics-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trends} margin={{ top: 12, right: 16, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" minTickGap={24} />
                      <YAxis allowDecimals={false} />
                      <Tooltip formatter={(value, name) => [formatNumber(value), name]} />
                      <Legend />
                      <Line type="monotone" dataKey="successfulLogins" name="Success" stroke="#2e7d32" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="failedLogins" name="Failed" stroke="#ed6c02" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </DataState>
            </section>

            <section className="login-analytics-panel" aria-labelledby="failure-reasons-title">
              <h2 id="failure-reasons-title">Failure Reasons Breakdown</h2>
              <DataState
                loading={false}
                empty={failureReasons.length === 0}
                emptyTitle="No failures"
                emptyText="No failed login reasons were recorded for this range."
              >
                <div className="login-analytics-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={failureReasons} margin={{ top: 12, right: 16, left: 0, bottom: 48 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="reason"
                        interval={0}
                        angle={-28}
                        textAnchor="end"
                        tickFormatter={prettifyFailureReason}
                        height={72}
                      />
                      <YAxis allowDecimals={false} />
                      <Tooltip
                        labelFormatter={prettifyFailureReason}
                        formatter={(value) => [formatNumber(value), "Attempts"]}
                      />
                      <Bar dataKey="attempts" name="Attempts" fill="#37446f" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </DataState>
            </section>
          </div>
        </DataState>

        <div className="login-analytics-tables">
          <section className="login-analytics-panel" aria-labelledby="problem-users-title">
            <div className="login-analytics-table-header">
              <h2 id="problem-users-title">Problem Users/Usernames</h2>
              <TextField
                label="Search username or user id"
                size="small"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ minWidth: { xs: "100%", sm: 300 } }}
              />
            </div>
            <DataState
              loading={usersLoading}
              error={usersError}
              empty={users.length === 0}
              emptyTitle="No problem users found"
              emptyText="No usernames match the selected range and search."
              retry={loadUsers}
            >
              <div className="login-analytics-table-wrap">
                <table className="login-analytics-table">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Total</th>
                      <th>Success</th>
                      <th>Failed</th>
                      <th>Failure Rate</th>
                      <th>Last Attempt</th>
                      <th>Last Status</th>
                      <th>Last Failure</th>
                      <th>Last Device</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={`${user.username || "unknown"}-${user.userId || "unknown"}`}>
                        <td data-label="Username">
                          <span className="login-analytics-user-cell">
                            <strong>{user.username || "Unknown username"}</strong>
                            <span className="login-analytics-muted">{user.userId || "Unknown user"}</span>
                          </span>
                        </td>
                        <td data-label="Total">{formatNumber(user.totalAttempts)}</td>
                        <td data-label="Success">{formatNumber(user.successfulLogins)}</td>
                        <td data-label="Failed">{formatNumber(user.failedLogins)}</td>
                        <td data-label="Failure Rate">{formatPercent(user.failureRate)}</td>
                        <td data-label="Last Attempt">{formatDateTime(user.lastAttemptAt)}</td>
                        <td data-label="Last Status"><StatusChip status={user.lastStatus} /></td>
                        <td data-label="Last Failure">{displayFailureReason(user.lastFailureReason)}</td>
                        <td data-label="Last Device">
                          <span className="login-analytics-device" title={user.lastDevice || ""}>
                            {user.lastDevice || "-"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <TablePagination
                component="div"
                count={usersTotal}
                page={usersPage}
                rowsPerPage={TABLE_LIMIT}
                rowsPerPageOptions={[TABLE_LIMIT]}
                onPageChange={(_, nextPage) => setUsersPage(nextPage)}
              />
            </DataState>
          </section>

          <section className="login-analytics-panel" aria-labelledby="recent-attempts-title">
            <div className="login-analytics-table-header">
              <h2 id="recent-attempts-title">Recent Login Attempts</h2>
              <Box className="login-analytics-filters">
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel id="status-filter-label">Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    label="Status"
                    value={status}
                    onChange={(event) => setStatus(event.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="success">Success</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="failure-filter-label">Failure reason</InputLabel>
                  <Select
                    labelId="failure-filter-label"
                    label="Failure reason"
                    value={failureReason}
                    onChange={(event) => setFailureReason(event.target.value)}
                  >
                    <MenuItem value="">All failure reasons</MenuItem>
                    {failureReasonOptions.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {prettifyFailureReason(reason)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </div>
            <DataState
              loading={recentLoading}
              error={recentError}
              empty={recentAttempts.length === 0}
              emptyTitle="No recent attempts found"
              emptyText="No login attempts match the selected filters."
              retry={loadRecentAttempts}
            >
              <div className="login-analytics-table-wrap">
                <table className="login-analytics-table">
                  <thead>
                    <tr>
                      <th>Created At</th>
                      <th>Username</th>
                      <th>User</th>
                      <th>Status</th>
                      <th>Failure Reason</th>
                      <th>Device</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentAttempts.map((attempt, index) => (
                      <tr key={attempt.id || `${attempt.createdAt || "attempt"}-${index}`}>
                        <td data-label="Created At">{formatDateTime(attempt.createdAt)}</td>
                        <td data-label="Username">{attempt.username || "Unknown username"}</td>
                        <td data-label="User">{attempt.userId || "Unknown user"}</td>
                        <td data-label="Status"><StatusChip status={attempt.status} /></td>
                        <td data-label="Failure Reason">{displayFailureReason(attempt.failureReason)}</td>
                        <td data-label="Device">
                          <span className="login-analytics-device" title={attempt.device || ""}>
                            {attempt.device || "-"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <TablePagination
                component="div"
                count={recentTotal}
                page={recentPage}
                rowsPerPage={TABLE_LIMIT}
                rowsPerPageOptions={[TABLE_LIMIT]}
                onPageChange={(_, nextPage) => setRecentPage(nextPage)}
              />
            </DataState>
          </section>
        </div>
      </div>
    </div>
  );
};

export default LoginAnalytics;
